/**
 * Execution List Page
 * Main page for viewing all campaign executions with filtering
 */

import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { RefreshCw, Activity, CheckCircle, XCircle, Clock } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Select } from '@/components/ui/select'
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs'
import { LoadingState, ErrorState } from '@/components/ui/loading-state'
import { ExecutionTable } from '../components/ExecutionTable'
import { MetricCard } from '../components/MetricCard'
import { CampaignTypeBadge } from '../components/CampaignTypeBadge'
import { getMonitoringExecutions, getMonitoringStats } from '../queries/executions'
import type { ExecutionMonitoringResponse, CampaignType, MonitoringStatsResponse } from '../types'
import { useAccount } from '@/contexts/AccountContext'

// Filter options
const statusOptions = [
  { value: 'ALL', label: 'All Statuses' },
  { value: 'RUNNING', label: 'Running' },
  { value: 'DELIVERING', label: 'Delivering' },
  { value: 'COMPLETED', label: 'Completed' },
  { value: 'FAILED', label: 'Failed' },
]

const campaignTypeOptions = [
  { value: 'ALL', label: 'All Types' },
  { value: 'ONE_TIME', label: 'One-Time' },
  { value: 'RECURRING', label: 'Recurring' },
  { value: 'AB_TEST', label: 'A/B Test' },
]

const daysOptions = [
  { value: '1', label: 'Last 1 Day' },
  { value: '3', label: 'Last 3 Days' },
  { value: '7', label: 'Last 7 Days' },
  { value: '14', label: 'Last 14 Days' },
  { value: '30', label: 'Last 30 Days' },
]

export function ExecutionListPage() {
  const [statusFilter, setStatusFilter] = useState('ALL')
  const [typeFilter, setTypeFilter] = useState('ALL')
  const [daysFilter, setDaysFilter] = useState('1')
  const [activeTab, setActiveTab] = useState('all')

  // Use global account context
  const { selectedAccountId } = useAccount()

  // Parse account ID safely
  const parsedAccountId = selectedAccountId && selectedAccountId !== 'ALL'
    ? parseInt(selectedAccountId)
    : undefined

  // Fetch executions
  const {
    data: executions = [],
    isLoading,
    error,
    refetch,
  } = useQuery<ExecutionMonitoringResponse[]>({
    queryKey: ['executions', statusFilter, typeFilter, daysFilter, selectedAccountId],
    queryFn: () =>
      getMonitoringExecutions(
        100,
        statusFilter !== 'ALL' ? statusFilter : undefined,
        typeFilter !== 'ALL' ? (typeFilter as CampaignType) : undefined,
        parseInt(daysFilter),
        parsedAccountId
      ),
    refetchInterval: 10000, // Refresh every 10 seconds
  })

  // Fetch stats
  const { data: stats } = useQuery<MonitoringStatsResponse>({
    queryKey: ['execution-stats', daysFilter, selectedAccountId],
    queryFn: () => getMonitoringStats(
      parseInt(daysFilter),
      parsedAccountId
    ),
    refetchInterval: 10000,
  })

  // Filter executions by tab
  const filteredExecutions = executions.filter((exec) => {
    if (activeTab === 'all') return true
    if (activeTab === 'active') return exec.isActive
    if (activeTab === 'completed') return exec.status === 'COMPLETED'
    if (activeTab === 'failed') return exec.status === 'FAILED'
    return true
  })

  // Group by campaign type
  const oneTimeExecutions = filteredExecutions.filter((e) => e.campaignType === 'ONE_TIME')
  const recurringExecutions = filteredExecutions.filter((e) => e.campaignType === 'RECURRING')
  const abTestExecutions = filteredExecutions.filter((e) => e.campaignType === 'AB_TEST')

  if (error) {
    return (
      <div className="p-6">
        <ErrorState
          error={error as Error}
          message="Failed to load executions"
          onRetry={() => refetch()}
        />
      </div>
    )
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Execution Monitoring</h1>
          <p className="text-gray-500 mt-1">
            Monitor campaign executions with flexible time periods and account filters
          </p>
        </div>
        <Button variant="outline" onClick={() => refetch()}>
          <RefreshCw className="h-4 w-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <MetricCard
            title={`Total Executions (${daysFilter} day${parseInt(daysFilter) > 1 ? 's' : ''})`}
            value={stats.totalExecutions}
            icon={<Activity className="h-5 w-5" />}
            color="blue"
          />
          <MetricCard
            title="Active Now"
            value={stats.activeExecutions}
            icon={<Clock className="h-5 w-5" />}
            color="yellow"
          />
          <MetricCard
            title="Completed"
            value={stats.byStatus?.COMPLETED || 0}
            icon={<CheckCircle className="h-5 w-5" />}
            color="green"
          />
          <MetricCard
            title="Failed"
            value={stats.byStatus?.FAILED || 0}
            icon={<XCircle className="h-5 w-5" />}
            color="red"
          />
        </div>
      )}

      {/* Campaign Type Summary */}
      {stats && (
        <div className="flex gap-4 flex-wrap">
          <div className="flex items-center gap-2 bg-white px-4 py-2 rounded-lg border shadow-sm">
            <CampaignTypeBadge type="ONE_TIME" />
            <span className="font-medium">{stats.byCampaignType?.ONE_TIME || 0}</span>
          </div>
          <div className="flex items-center gap-2 bg-white px-4 py-2 rounded-lg border shadow-sm">
            <CampaignTypeBadge type="RECURRING" />
            <span className="font-medium">{stats.byCampaignType?.RECURRING || 0}</span>
          </div>
          <div className="flex items-center gap-2 bg-white px-4 py-2 rounded-lg border shadow-sm">
            <CampaignTypeBadge type="AB_TEST" />
            <span className="font-medium">{stats.byCampaignType?.AB_TEST || 0}</span>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white p-4 rounded-lg border shadow-sm">
        <div className="flex gap-4 items-center flex-wrap">
          <div className="w-48">
            <label className="block text-xs font-medium text-gray-700 mb-1">Time Period</label>
            <Select
              options={daysOptions}
              value={daysFilter}
              onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setDaysFilter(e.target.value)}
            />
          </div>
          <div className="w-48">
            <label className="block text-xs font-medium text-gray-700 mb-1">Status</label>
            <Select
              options={statusOptions}
              value={statusFilter}
              onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setStatusFilter(e.target.value)}
            />
          </div>
          <div className="w-48">
            <label className="block text-xs font-medium text-gray-700 mb-1">Campaign Type</label>
            <Select
              options={campaignTypeOptions}
              value={typeFilter}
              onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setTypeFilter(e.target.value)}
            />
          </div>
        </div>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="all" value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="all">
            All ({executions.length})
          </TabsTrigger>
          <TabsTrigger value="active">
            Active ({executions.filter((e) => e.isActive).length})
          </TabsTrigger>
          <TabsTrigger value="completed">
            Completed ({executions.filter((e) => e.status === 'COMPLETED').length})
          </TabsTrigger>
          <TabsTrigger value="failed">
            Failed ({executions.filter((e) => e.status === 'FAILED').length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="all">
          {isLoading ? (
            <LoadingState message="Loading executions..." />
          ) : (
            <div className="space-y-8">
              {/* One-Time Campaigns */}
              {oneTimeExecutions.length > 0 && (
                <div>
                  <div className="flex items-center gap-2 mb-4">
                    <CampaignTypeBadge type="ONE_TIME" />
                    <span className="text-lg font-semibold">One-Time Campaigns</span>
                    <span className="text-gray-500">({oneTimeExecutions.length})</span>
                  </div>
                  <ExecutionTable executions={oneTimeExecutions} />
                </div>
              )}

              {/* Recurring Campaigns */}
              {recurringExecutions.length > 0 && (
                <div>
                  <div className="flex items-center gap-2 mb-4">
                    <CampaignTypeBadge type="RECURRING" />
                    <span className="text-lg font-semibold">Recurring Campaigns</span>
                    <span className="text-gray-500">({recurringExecutions.length})</span>
                  </div>
                  <ExecutionTable executions={recurringExecutions} />
                </div>
              )}

              {/* A/B Test Campaigns */}
              {abTestExecutions.length > 0 && (
                <div>
                  <div className="flex items-center gap-2 mb-4">
                    <CampaignTypeBadge type="AB_TEST" />
                    <span className="text-lg font-semibold">A/B Test Campaigns</span>
                    <span className="text-gray-500">({abTestExecutions.length})</span>
                  </div>
                  <ExecutionTable executions={abTestExecutions} />
                </div>
              )}

              {filteredExecutions.length === 0 && (
                <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
                  <Activity className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900">No executions found</h3>
                  <p className="text-gray-500 mt-1">
                    Try adjusting your filters or wait for new executions.
                  </p>
                </div>
              )}
            </div>
          )}
        </TabsContent>

        <TabsContent value="active">
          <ExecutionTable
            executions={filteredExecutions}
            isLoading={isLoading}
          />
        </TabsContent>

        <TabsContent value="completed">
          <ExecutionTable
            executions={filteredExecutions}
            isLoading={isLoading}
          />
        </TabsContent>

        <TabsContent value="failed">
          <ExecutionTable
            executions={filteredExecutions}
            isLoading={isLoading}
          />
        </TabsContent>
      </Tabs>
    </div>
  )
}

export default ExecutionListPage
